import { Controller } from "@hotwired/stimulus";

/**
 * Plans Controller - Plan selection & state management

 * Responsibilities:
 * • Plan card selection & visual state (selected/unselected)
 * • Plan button text updates (Select/Change/Keep/Current)
 * • Change mode activation & premium auto-selection
 * • Enterprise vs basic/premium plan mode switching
 * • Inter-controller communication via plan-changed events
 * • Backend-driven category detection via data-category attributes

 * Replaces jQuery:
 * • Plan.selectPlan() - plan card clicking & selection
 * • Plan.change() / Plan.unchange() - change mode toggling
 * • Plan button state management & text updates
 */

export default class extends Controller {
  static DEFAULTS = {
    SELECTED_CLASS: "selected",
    PLAN_ACTION_SELECTED: "plan-action selected-plan",
    PLAN_ACTION_DEFAULT: "plan-action select-plan",
    YEAR: "year",
    MONTH: "month"
  };

  static targets = [
    "planCard",
    "planButton",
    "cardsWrapper",
    "monthlyButton",
    "yearlyButton",
    "continueButton",
    "addonSection",
    "addonContinueButton",
    "existingAddonsUpdateButton",
    "newPlan",
    "newPlanPayment",
    "switchPaymentProvider",
    "showPaymentProvider",
    "cancellationForm"
  ];

  static values = {
    selectedCategory: String,
    currentCategory: String,
    currentRecurrence: String,
    currentPlan: String,
    currentPlanLevel: Number,
    recurrence: String,
    prefillPlanType: String,
    autoExpand: Boolean,
    buildPlanName: String,
    buildPlanRecurrence: String
  };

  connect() {
    // Initialize recurrence value
    this.recurrenceValue = this.currentRecurrenceValue || this.constructor.DEFAULTS.YEAR;

    // Set selected category from backend (if on a plan)
    if (this.currentCategoryValue) this.selectedCategoryValue = this.currentCategoryValue;

    this.updatePlanSelection();
    this.updateRecurrenceDisplay();
    this.handleChangeModeSelection();

    // Listen for any plan-changed events (including from pricing controller)
    this.element.addEventListener("pricing:plan-changed", this.handlePlanChanged);
    this.element.addEventListener("pricing:prefill-features-applied", this.handlePrefillFeaturesApplied);

    // Defer so pricing_controller is connected before we dispatch on the viewer dropdown.
    if (this.prefillPlanTypeValue) queueMicrotask(() => this.applyPrefillPlanType());

    // ?expand=true drops the user straight into the change-plan flow.
    if (this.autoExpandValue) queueMicrotask(() => this.enterChangeMode());

    // ?build_plan=X&build_plan_recurrence=Y URL pins the plan/recurrence so "Continue to billing" submits the right tier.
    if (this.buildPlanNameValue && this.buildPlanRecurrenceValue) {
      this.recurrenceValue = this.buildPlanRecurrenceValue;
    }
  }

  disconnect() {
    this.element.removeEventListener("pricing:plan-changed", this.handlePlanChanged);
    this.element.removeEventListener("pricing:prefill-features-applied", this.handlePrefillFeaturesApplied);
  }

  // Addon-flow auto-progress. Plan-flow is handled in applyPrefillPlanType.
  handlePrefillFeaturesApplied = () => {
    const planFlowActive = this.prefillPlanTypeValue;
    if (planFlowActive) return;
    if (this.hasExistingAddonsUpdateButtonTarget) this.existingAddonsUpdateButtonTarget.click();
  };

  // Arrow function automatically binds 'this' context
  handlePlanChanged = (event) => {
    const { category, planName } = event.detail;
    if (category) {
      if (category !== this.selectedCategoryValue) {
        this.changePlanSelection(category);
        this.showContinueButton();
      } else {
        this.updateButtonTextForViewerChange(category);
      }
    }
  };

  selectPlan(event) {
    const card = event.currentTarget.closest("[data-category]");
    if (!card) return;

    this.changePlanSelection(card.dataset.category);
    this.showContinueButton();
  }

  applyPrefillPlanType() {
    const option = this.element.querySelector(`option[data-plan="${this.prefillPlanTypeValue}"]`);
    const card = option?.closest("[data-category]") ||
                 this.planCardTargets.find(c => c.dataset.plan === this.prefillPlanTypeValue);
    if (!card) return;

    // activateChangeMode keeps the current category when it's already selectable, but the
    // prefill wants the upgrade target — so set the category explicitly after.
    this.activateChangeMode();
    this.changePlanSelection(card.dataset.category);

    if (option) {
      const select = option.closest("select");
      if (select) {
        select.value = option.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    this.showContinueButton();

    // Two-phase defer: showAddonSection triggers an async price refetch, and we want the
    // Continue click to happen after the section has mounted.
    queueMicrotask(() => {
      this.showAddonSection();
      queueMicrotask(() => {
        if (this.hasAddonContinueButtonTarget) this.addonContinueButtonTarget.click();
      });
    });
  }

  getSelectedPlanType() {
    const category = this.selectedCategoryValue;
    const card = this.findPlanCardByCategory(category);

    if (!card) return null;

    // For plans with viewer dropdown (like premium), get the selected viewer count plan
    const capabilities = JSON.parse(card.dataset.capabilities || "{}");
    if (capabilities.hasSelectableViewerLimit) {
      const pricingController = this.getController("pricing");
      const viewerPlan = pricingController?.selectedPlanValue;
      if (viewerPlan) return viewerPlan;
    }

    // Otherwise return the card's plan name
    return card.dataset.plan;
  }

  // Centralized plan selection logic
  changePlanSelection(category) {
    if (category === this.selectedCategoryValue) return;

    this.selectedCategoryValue = category;
    this.updatePlanSelection();
    this.notifyPricingController();

    // If addon section is already visible, re-filter and reopen groups
    if (this.hasAddonSectionTarget && this.addonSectionTarget.style.display !== "none") {
      const pricingController = this.getController("pricing");
      if (pricingController) {
        pricingController.filterInlineAddonsByPlan();
        pricingController.refreshPricingForCategory(category);
      }
      this.openAddonGroups();
    }

  }

  cancelChangeMode() {
    this.element.classList.remove("is-changing", "is-changing-addons");
    this.hideAddonAndBillingSections();
  }

  // Single entry point for "enter the plan-change flow" — used by Change plan buttons
  // and by the ?expand=true URL auto-trigger.
  enterChangeMode() {
    this.hideAddonAndBillingSections();
    this.activateChangeMode();
  }

  activateChangeMode() {
    // Toggle change mode UI state
    this.element.classList.add("is-changing");

    // Only auto-select premium if current category is not selectable
    const card = this.findPlanCardByCategory(this.selectedCategoryValue);
    const capabilities = card ? JSON.parse(card.dataset.capabilities || "{}") : {};

    if (capabilities.selectable) {
      this.showContinueButton();
      return;
    }

    // Select the highest tier selectable plan (last in array, typically premium)
    const selectableCard = this.planCardTargets.findLast(c => JSON.parse(c.dataset.capabilities || "{}").selectable);
    if (selectableCard) {
      this.changePlanSelection(selectableCard.dataset.category);
      this.showContinueButton();
    }
  }

  buildPausePlan(event) {
    event.preventDefault();
    const PAUSE_PLAN_TYPE = "pause";
    if (!this.hasNewPlanTarget) return;

    const params = new URLSearchParams();
    params.append("plan[plan_type_name]", PAUSE_PLAN_TYPE);
    params.append("plan[recurrence]", this.recurrenceValue);

    this.toggleLoader(true);

    fetch(`${this.newPlanTarget.dataset.path}?${params.toString()}`, { headers: { Accept: "text/html" } })
      .then(response => {
        if (!response.ok) throw new Error("Request failed");
        return response.text();
      })
      .then(html => {
        this.newPlanTarget.innerHTML = html;

        if (this.hasNewPlanPaymentTarget) this.newPlanPaymentTarget.innerHTML = "";
        if (this.hasSwitchPaymentProviderTarget) this.switchPaymentProviderTarget.innerHTML = "";

        this.getController("currency")?.updateDisplay();
        this.newPlanTarget.scrollIntoView({behavior: "smooth", block: "start"});
      })
      .catch(() => {
        this.getController("payment")?.showError(
          I18n?.t?.("js.error_has_occurred") || "An error occurred"
        );
      })
      .finally(() => {
        this.toggleLoader(false);
      });
  }

  showCancellationForm(event) {
    event.preventDefault();
    this.cancellationFormTarget.style.setProperty("display", "block");
  }

  hideCancellationForm(event) {
    event.preventDefault();
    this.cancellationFormTarget.style.setProperty("display", "none");
  }

  prepareCancellation(event) {
    event.preventDefault();
    const CANCEL_NOW = "1";
    const PAID_PAUSE = "4";
    const option = document.querySelector("input[name=\"cancellation_option\"]:checked")?.value;

    if (option === CANCEL_NOW || !option) {
      const cancelPath = event.params.cancelPath;
      if (cancelPath) window.location.href = cancelPath;
    } else if (option === PAID_PAUSE) {
      this.cancellationFormTarget.style.setProperty("display", "none");
      this.buildPausePlan(event);
    }
  }

  findPlanCardByCategory(category) {
    return this.planCardTargets.find(card => card.dataset.category === category);
  }

  updatePlanSelection() {
    this.planCardTargets.forEach(card => {
      const cardCategory = card.dataset.category;
      const isSelected = this.selectedCategoryValue === cardCategory;
      const capabilities = JSON.parse(card.dataset.capabilities || "{}");

      // Only apply selection styling to selectable plans
      if (capabilities.selectable) {
        card.classList.toggle(this.constructor.DEFAULTS.SELECTED_CLASS, isSelected);

        const button = card.querySelector("[data-plans-target='planButton']");
        if (button) {
          this.updateButtonState(button, isSelected);
        }
      }
    });

    // Update recurrence display based on selected plan
    this.updateRecurrenceDisplay();
  }

  // Toggles button text between default ("Subscribe") and selected ("Selected plan") states
  updateButtonState(button, isSelected) {
    const defaultSpan = button.querySelector("[data-plan-button-text='default']");
    const selectedSpan = button.querySelector("[data-plan-button-text='selected']");

    if (defaultSpan && selectedSpan) {
      if (isSelected) {
        defaultSpan.style.display = "none";
        selectedSpan.style.display = "inline";
        button.className = this.constructor.DEFAULTS.PLAN_ACTION_SELECTED;
      } else {
        defaultSpan.style.display = "inline";
        selectedSpan.style.display = "none";
        button.className = button.dataset.defaultClass || this.constructor.DEFAULTS.PLAN_ACTION_DEFAULT;
      }
    }
  }

  updateButtonTextForViewerChange(category) {
    const card = this.findPlanCardByCategory(category);
    if (!card) return;

    const capabilities = JSON.parse(card.dataset.capabilities || "{}");
    if (!capabilities.hasSelectableViewerLimit) return;

    const button = card.querySelector("[data-plans-target='planButton']");
    const selectedSpan = button.querySelector("[data-plan-button-text='selected']");
    if (!selectedSpan || !selectedSpan.dataset.keepText) return;

    const labelSpan = selectedSpan.querySelector("[data-plan-button-label]");
    if (!labelSpan) return;

    const viewerSelect = card.querySelector("[data-pricing-target='viewerSelect']");
    if (!viewerSelect) return;

    const selectedOption = viewerSelect.options[viewerSelect.selectedIndex];
    const selectedLevel = Number(selectedOption?.dataset.level);

    if (selectedLevel === this.currentPlanLevelValue) {
      labelSpan.textContent = selectedSpan.dataset.keepText;
    } else if (selectedLevel > this.currentPlanLevelValue) {
      labelSpan.textContent = selectedSpan.dataset.selectedText;
    } else {
      labelSpan.textContent = selectedSpan.dataset.downgradeText;
    }
  }

  handleChangeModeSelection() {
    if (this.element.classList.contains("is-changing")) {
      this.activateChangeMode();
    }
  }

  updateRecurrenceDisplay() {
    const isMonthly = this.recurrenceValue === this.constructor.DEFAULTS.MONTH;
    const card = this.findPlanCardByCategory(this.selectedCategoryValue);
    const capabilities = card ? JSON.parse(card.dataset.capabilities || "{}") : {};
    const showToggle = capabilities.showMonthlyYearlyToggle;

    this.monthlyButtonTargets.forEach(btn => btn.classList.toggle("active", isMonthly && showToggle));
    this.yearlyButtonTargets.forEach(btn => btn.classList.toggle("active", !isMonthly && showToggle));
  }

  notifyPricingController() {
    const card = this.findPlanCardByCategory(this.selectedCategoryValue);
    if (!card) return;

    this.dispatch("plan-changed", { detail: { planName: card.dataset.plan, category: this.selectedCategoryValue }, bubbles: true });
  }

  getController(name) {
    const controllerKey = `_${name}Controller`;
    if (this[controllerKey]) return this[controllerKey];

    this[controllerKey] = this.application.getControllerForElementAndIdentifier(
      this.element,
      name
    );

    return this[controllerKey];
  }

  addonParamsForSection(section) {
    const addons = {};
    if (!section) return {features: {}, once: {}};

    section.querySelectorAll("input[type='checkbox']").forEach(input => {
      if (input.checked) addons[input.name] = 1;
    });

    section.querySelectorAll(".quantity-input").forEach(input => {
      const value = parseInt(input.value, 10) || 0;
      if (value > 0) addons[input.name] = value;
    });

    const pricingController = this.getController("pricing");
    if (!pricingController?.splitRecurringAndOneTimeAddons) return {features: {}, once: {}};

    const {recurring, once} = pricingController.splitRecurringAndOneTimeAddons(addons);
    return {features: recurring, once};
  }

  setRecurrence(event) {
    event.preventDefault();

    const period = event.currentTarget.dataset.period;
    if (![this.constructor.DEFAULTS.MONTH, this.constructor.DEFAULTS.YEAR].includes(period)) return;

    this.recurrenceValue = period;
    this.updateRecurrenceDisplay();

    // Notify pricing controller
    this.dispatch("recurrence-changed", {
      detail: { recurrence: period },
      bubbles: true
    });
  }

  switchToAnnual(event) {
    event?.preventDefault();

    this.recurrenceValue = this.constructor.DEFAULTS.YEAR;
    this.updateRecurrenceDisplay();
    this.dispatch("recurrence-changed", {
      detail: {recurrence: this.recurrenceValue},
      bubbles: true
    });

    this.trackEvent("annual_upsell_clicked");
    this.buildPlan(event);
  }

  showUnselectablePlans(event) {
    event?.preventDefault();
    event?.stopPropagation();

    this.hasCardsWrapperTarget && this.cardsWrapperTarget.classList.add("disabled-card-selected");

    const unselectableCard = this.planCardTargets.find(c =>
      !JSON.parse(c.dataset.capabilities || "{}").selectable
    );
    if (unselectableCard) {
      this.changePlanSelection(unselectableCard.dataset.category);
      this.showContinueButton();
    }
  }

  showSelectablePlans(event) {
    event?.preventDefault();

    this.hasCardsWrapperTarget && this.cardsWrapperTarget.classList.remove("disabled-card-selected");

    this.activateChangeMode();
    this.showContinueButton();
  }

  showAddonSection(event) {
    event?.preventDefault();

    if (!this.hasAddonSectionTarget) return;

    // Filter addons for the selected plan category and fetch fresh addon prices
    const pricingController = this.getController("pricing");
    if (pricingController) {
      pricingController.filterInlineAddonsByPlan();
      pricingController.refreshPricingForCategory(this.selectedCategoryValue);
    }

    this.addonSectionTarget.style.display = "";
    this.addonSectionTarget.scrollIntoView({ behavior: "smooth", block: "start" });

    // Track plan selection for new pricing plans
    if (window.shouldTrackNewPricing) {
      this.trackEvent("new_pricing_plan_selected");
    }

    // Hide this button — addon section handles everything from here
    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.style.display = "none";
    }
  }

  buildPlan(event) {
    event?.preventDefault();

    if (!this.hasNewPlanTarget) return;

    const path = this.newPlanTarget.dataset.path;
    if (!path) return;

    const useExistingPlan = event?.params?.existingPlan === true;
    const clickedButton = event?.currentTarget;
    // Triggers outside the addon section (e.g. the annual upsell banner) still need to
    // keep the chosen addons, so fall back to the visible addon section.
    const addonSection = clickedButton?.closest(".addons-section") ||
      (this.hasAddonSectionTarget ? this.addonSectionTarget : null);

    // Collapse the clicked section's open groups to keep the view tidy while billing loads
    addonSection?.querySelectorAll(".closeable-group.group-opened").forEach(group => {
      group.querySelector(".closeable-group-header")?.click();
    });

    const params = this.buildPlanParams(useExistingPlan, addonSection);

    this.toggleLoader(true);

    fetch(`${path}?${params.toString()}`, {headers: {"Accept": "text/html"}})
      .then(response => {
        if (!response.ok) throw new Error("Request failed");
        return response.text();
      })
      .then(html => {
        if (window.$) {
          window.$(this.newPlanTarget).html(html);
        } else {
          this.newPlanTarget.innerHTML = html;
        }

        if (this.hasNewPlanPaymentTarget) this.newPlanPaymentTarget.innerHTML = "";
        if (this.hasSwitchPaymentProviderTarget) this.switchPaymentProviderTarget.innerHTML = "";

        // Currency controller will automatically convert newly injected [data-base-amount] elements
        // when it receives the mutation or on next currency change
        // Manually trigger display update for newly injected content
        this.getController("currency")?.updateDisplay();

        this.newPlanTarget.scrollIntoView({behavior: "smooth", block: "start"});

        // After first billing load, change the clicked button's text to "Update"
        if (clickedButton) {
          const updateText = clickedButton.dataset.updateText;
          if (updateText) clickedButton.textContent = updateText;
        }

        // Flip the page into addon-change mode. CSS reveals the addon cancel button
        // and hides billing. Cancel/complete reloads the page, so state clears.
        if (useExistingPlan) {
          this.element.classList.add("is-changing-addons");
        }
      })
      .catch(() => {
        this.getController("payment")?.showError(
          I18n?.t?.("js.error_has_occurred") || "An error occurred"
        );
      })
      .finally(() => {
        this.toggleLoader(false);
      });
  }

  buildPlanParams(useExistingPlan = false, addonSection = null) {
    const params = new URLSearchParams();
    // build_plan URL pins the plan tier; skip card/dropdown resolution.
    const planType = useExistingPlan ? this.currentPlanValue : (this.buildPlanNameValue || this.getSelectedPlanType());

    params.append("plan[plan_type_name]", planType);
    params.append("plan[recurrence]", this.recurrenceValue);
    if (useExistingPlan) params.append("existing_plan_update", "1");

    const { features, once } = this.addonParamsForSection(addonSection);

    Object.entries(features).forEach(([key, quantity]) => {
      params.append(`plan[features][${key}]`, quantity);
    });

    Object.entries(once).forEach(([key, quantity]) => {
      params.append(`plan[once][${key}]`, quantity);
    });

    return params;
  }

  toggleLoader(show) {
    const loader = document.querySelector(".loader");
    if (!loader) return;

    loader.style.display = show ? "block" : "none";
  }

  trackEvent(eventType) {
    fetch("/admin/app_events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
      },
      body: JSON.stringify({ event_type: eventType })
    }).catch(error => console.error("Tracking error:", error));
  }

  showContinueButton() {
    if (this.hasContinueButtonTarget) {
      // Hide button if no plan is selected or if enterprise/unselectable is selected
      const card = this.findPlanCardByCategory(this.selectedCategoryValue);
      const capabilities = card ? JSON.parse(card.dataset.capabilities || "{}") : {};
      const isSelectable = this.selectedCategoryValue && capabilities.selectable;
      const addonSectionVisible = this.hasAddonSectionTarget && this.addonSectionTarget.style.display !== "none";
      this.continueButtonTarget.style.display = isSelectable && !addonSectionVisible ? "" : "none";

      if (!isSelectable) {
        this.hideAddonAndBillingSections();
      }
    }
  }

  openAddonGroups() {
    if (!this.hasAddonSectionTarget) return;

    this.addonSectionTarget.querySelectorAll(".closeable-group.group-closed").forEach(group => {
      if (group.style.display !== "none") {
        group.querySelector(".closeable-group-header")?.click();
      }
    });
  }

  hideAddonAndBillingSections() {
    if (this.hasAddonSectionTarget) {
      this.addonSectionTarget.style.display = "none";
    }

    if (this.hasNewPlanTarget) this.newPlanTarget.innerHTML = "";
    if (this.hasNewPlanPaymentTarget) this.newPlanPaymentTarget.innerHTML = "";
    if (this.hasSwitchPaymentProviderTarget) this.switchPaymentProviderTarget.innerHTML = "";
    if (this.hasShowPaymentProviderTarget) this.showPaymentProviderTarget.innerHTML = "";
  }
}
